import type { Tour, FilterState } from '@/types/tour';

export function getLowestPrice(tour: Tour): number | null {
  if (!tour.tour_platforms || tour.tour_platforms.length === 0) return null;
  return Math.min(...tour.tour_platforms.map(p => Number(p.price)));
}

export function getAggregatedReview(tour: Tour): { rating: number; count: number } | null {
  if (!tour.tour_platforms || tour.tour_platforms.length === 0) return null;
  const rated = tour.tour_platforms.filter(p => p.review_rating !== null && p.review_count !== null);
  if (rated.length === 0) return null;
  const totalCount = rated.reduce((sum, p) => sum + (p.review_count || 0), 0);
  const weightedRating = rated.reduce((sum, p) => sum + (p.review_rating || 0) * (p.review_count || 0), 0) / totalCount;
  return { rating: Number(weightedRating.toFixed(1)), count: totalCount };
}

export function filterTours(tours: Tour[], filters: FilterState): Tour[] {
  return tours.filter(tour => {
    if (filters.company_name.length > 0 && (!tour.company_name || !filters.company_name.includes(tour.company_name))) return false;
    if (filters.vibe.length > 0 && (!tour.vibe || !filters.vibe.includes(tour.vibe))) return false;
    if (filters.crowd_size.length > 0 && (!tour.crowd_size || !filters.crowd_size.includes(tour.crowd_size))) return false;
    if (filters.boarding.length > 0 && (!tour.boarding || !filters.boarding.includes(tour.boarding))) return false;
    if (filters.best_for.length > 0 && (!tour.best_for || !filters.best_for.some(b => tour.best_for!.includes(b)))) return false;
    if (filters.available_to.length > 0 && (!tour.available_to || !filters.available_to.some(a => tour.available_to!.includes(a)))) return false;
    if (filters.boat_type.length > 0 && (!tour.boat_type || !filters.boat_type.includes(tour.boat_type))) return false;
    if (filters.categories.length > 0 && (!tour.categories || !filters.categories.some(c => tour.categories!.includes(c)))) return false;
    if (filters.departure_point.length > 0 && (!tour.departure_point || !filters.departure_point.includes(tour.departure_point))) return false;
    if (filters.pick_up.length > 0 && (!tour.pick_up || !filters.pick_up.includes(tour.pick_up))) return false;
    if (filters.time_of_day.length > 0 && (!tour.time_of_day || !filters.time_of_day.some(v => tour.time_of_day!.includes(v)))) return false;

    if (filters.inclusions.length > 0) {
      if (!tour.inclusions || !filters.inclusions.every(i => tour.inclusions!.includes(i))) return false;
    }

    const price = getLowestPrice(tour);
    if (filters.price_min !== null && (price === null || price < filters.price_min)) return false;
    if (filters.price_max !== null && (price === null || price > filters.price_max)) return false;

    if (filters.review_min !== null) {
      const agg = getAggregatedReview(tour);
      if (!agg || agg.rating < filters.review_min) return false;
    }

    if (filters.duration_min !== null && (tour.duration_hours === null || tour.duration_hours < filters.duration_min)) return false;
    if (filters.duration_max !== null && (tour.duration_hours === null || tour.duration_hours > filters.duration_max)) return false;

    if (filters.snorkeling_no) {
      if (tour.snorkeling_quality !== 'No snorkeling') return false;
    } else if (filters.snorkeling_min !== null) {
      const v = parseRating(tour.snorkeling_quality);
      if (!v || v < filters.snorkeling_min) return false;
    }
    if (filters.food_no) {
      if (tour.food_quality !== 'No food') return false;
    } else if (filters.food_min !== null) {
      const v = parseRating(tour.food_quality);
      if (!v || v < filters.food_min) return false;
    }
    if (filters.open_bar_no) {
      if (tour.open_bar !== 'No open bar') return false;
    } else if (filters.open_bar_min !== null) {
      const v = parseRating(tour.open_bar);
      if (!v || v < filters.open_bar_min) return false;
    }
    if (filters.crew_no) {
      if (tour.crew !== 'No crew') return false;
    } else if (filters.crew_min !== null) {
      const v = parseRating(tour.crew);
      if (!v || v < filters.crew_min) return false;
    }

    return true;
  });
}

/** Returns true if the tour has a cheapest price worth highlighting (not all prices the same) */
export function hasHighlightedPrice(tour: Tour): boolean {
  const platforms = tour.tour_platforms;
  if (!platforms || platforms.length < 2) return false;
  const prices = platforms.map(p => Number(p.price));
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min < max; // highlight cheapest if not all the same
}

/** Get IDs of platforms with the cheapest price (for highlighting). Empty if all prices equal. */
export function getCheapestPlatformIds(tour: Tour): Set<string> {
  const platforms = tour.tour_platforms;
  if (!platforms || platforms.length < 2) return new Set();
  const prices = platforms.map(p => Number(p.price));
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return new Set(); // all same, no highlight
  return new Set(platforms.filter(p => Number(p.price) === min).map(p => p.id));
}

/** Sort tours: those with highlighted cheapest price first (by lowest price), then rest by lowest price */
export function sortToursWithPriority(tours: Tour[]): Tour[] {
  return [...tours].sort((a, b) => {
    const aHas = hasHighlightedPrice(a) ? 0 : 1;
    const bHas = hasHighlightedPrice(b) ? 0 : 1;
    if (aHas !== bHas) return aHas - bHas;
    const aPrice = getLowestPrice(a) ?? Infinity;
    const bPrice = getLowestPrice(b) ?? Infinity;
    return aPrice - bPrice;
  });
}

export function getAttributeColor(value: string | null): string {
  return 'bg-secondary text-secondary-foreground';
}

/** Parse a rating value ("1"-"5") to a number, returns null for non-numeric */
export function parseRating(value: string | null): number | null {
  if (!value) return null;
  // Handle "3|No alcohol" format
  const raw = value.includes('|') ? value.split('|')[0] : value;
  const n = parseInt(raw, 10);
  return n >= 1 && n <= 5 ? n : null;
}
